import { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { PermissionsProvider } from './context/PermissionsContext'
import { ProtectedRoute } from './components/layout/ProtectedRoute'
import { LayoutWithSidebar } from './components/layout/Layout'
import { ToastViewport } from './components/ui'
import { InstallPrompt } from './components/InstallPrompt'
import { startOnlineListener } from './offline/sync'

import LoginPage from './pages/LoginPage'
import DashboardPage from './pages/DashboardPage'
import OrdersPage from './pages/OrdersPage'
import OrderDetailPage from './pages/OrderDetailPage'
import LaunchpadPage from './pages/LaunchpadPage'
import RidersPage from './pages/RidersPage'
import CompliancePage from './pages/CompliancePage'
import FleetPage from './pages/FleetPage'
import PartnersPage from './pages/PartnersPage'
import FinancePage from './pages/FinancePage'
import CustomersPage from './pages/CustomersPage'
import UsersPage from './pages/UsersPage'
import BranchesPage from './pages/BranchesPage'
import ZonesPage from './pages/ZonesPage'
import PhysicsPricingPage from './pages/PhysicsPricingPage'
import SettingsPage from './pages/SettingsPage'
import AuditPage from './pages/AuditPage'
import TrackPage from './pages/TrackPage'
import RiderHome from './pages/rider/RiderHome'
import RiderLogin from './pages/rider/RiderLogin'
import PartnerLogin from './pages/partner/PartnerLogin'
import PartnerHome from './pages/partner/PartnerHome'
import PartnerIntakeForm from './pages/partner/PartnerIntakeForm'
import PartnerDataCollection from './pages/partner/PartnerDataCollection'
import CustomerLanding from './pages/customer/CustomerLanding'
import CustomerLookup from './pages/customer/CustomerLookup'
import CustomerOrderForm from './pages/customer/CustomerOrderForm'
import CustomerOrderConfirmation from './pages/customer/CustomerOrderConfirmation'
import LocatePage from './pages/customer/LocatePage'

type AppMode = 'admin' | 'rider' | 'customer' | 'partner'
const APP_MODE: AppMode = (import.meta.env.VITE_APP_MODE as AppMode) ?? 'admin'

function AdminApp() {
  const { user } = useAuth()
  useEffect(() => { startOnlineListener() }, [])
  return (
    <>
      <ToastViewport />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/track" element={<TrackPage />} />
        <Route path="/partner-intake" element={<PartnerIntakeForm />} />
        <Route path="/partner-data/:token" element={<PartnerDataCollection />} />
        <Route element={<ProtectedRoute />}>
          <Route element={<LayoutWithSidebar />}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/orders/:id" element={<OrderDetailPage />} />
            <Route path="/launchpad" element={<LaunchpadPage />} />
            <Route path="/customers" element={<CustomersPage />} />
            <Route path="/riders" element={<RidersPage />} />
            <Route path="/compliance" element={<CompliancePage />} />
            <Route path="/fleet" element={<FleetPage />} />
            <Route path="/partners" element={<PartnersPage />} />
            <Route path="/finance" element={<FinancePage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/branches" element={<BranchesPage />} />
            <Route path="/zones" element={<ZonesPage />} />
            <Route path="/physics-pricing" element={<PhysicsPricingPage />} />
            <Route path="/audit" element={<AuditPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to={user?.role === 'rider' ? '/rider' : '/'} replace />} />
      </Routes>
    </>
  )
}

function RiderApp() {
  const { user, loading, refresh } = useAuth()
  useEffect(() => { startOnlineListener() }, [])

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>
  }

  if (!user) {
    return (
      <>
        <ToastViewport />
        <RiderLogin onSuccess={async (_token, _rider) => { await refresh() }} />
      </>
    )
  }

  return (
    <>
      <ToastViewport />
      <Routes>
        <Route path="/" element={<RiderHome />} />
        <Route path="/rider" element={<RiderHome />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  )
}

function CustomerApp() {
  return (
    <>
      <ToastViewport />
      <Routes>
        <Route path="/" element={<CustomerLanding />} />
        <Route path="/order" element={<CustomerOrderForm />} />
        <Route path="/order-confirmation" element={<CustomerOrderConfirmation />} />
        <Route path="/locate" element={<LocatePage />} />
        <Route path="/track" element={<TrackPage />} />
        <Route path="/lookup" element={<CustomerLookup />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  )
}

function PartnerApp() {
  const { user, loading, refresh } = useAuth()
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>
  if (!user) {
    return (
      <>
        <ToastViewport />
        <PartnerLogin onSuccess={async () => { await refresh() }} />
      </>
    )
  }
  return (
    <>
      <ToastViewport />
      <Routes>
        <Route path="/" element={<PartnerHome />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  )
}

export default function App() {
  const shell =
    APP_MODE === 'customer' ? (
      <BrowserRouter><CustomerApp /></BrowserRouter>
    ) : APP_MODE === 'rider' ? (
      <AuthProvider>
        <PermissionsProvider>
          <BrowserRouter><RiderApp /></BrowserRouter>
        </PermissionsProvider>
      </AuthProvider>
    ) : APP_MODE === 'partner' ? (
      <AuthProvider>
        <PermissionsProvider>
          <BrowserRouter><PartnerApp /></BrowserRouter>
        </PermissionsProvider>
      </AuthProvider>
    ) : (
      <AuthProvider>
        <PermissionsProvider>
          <BrowserRouter><AdminApp /></BrowserRouter>
        </PermissionsProvider>
      </AuthProvider>
    )
  return (
    <>
      {shell}
      <InstallPrompt />
    </>
  )
}
